import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  useEventCallback,
} from '@mui/material';
import React, { memo, useMemo } from 'react';
import MunicipalityAutoComplete from 'system/components/shared/search-municipality';
import PmiTextField from 'shell/components/shared/text-field';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import MunicipalityForeignList from 'shell/components/shared/municipality-foreign-list';
import { noop } from 'shell/utils/noop';
import { DateTime } from 'luxon';
import { TypeRegister } from 'practice/constants/type-register';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResidenceOrigin } from 'shell/view-models/personal-data';
import { PmiButton } from 'shell/components/shared/button';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { AxiosPromise } from 'axios';
import { useParams } from 'react-router-dom';
// import { useFormikContext } from 'formik';
// import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { toast } from 'react-toastify';
import { praticePersonResidenceOriginUpdateTransformer } from 'practice/utils/practice-person-residence-origin-update-request-transformer';
import { useFormikContext } from 'formik';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';

interface ResidenceOriginFormProps {
  values?: ResidenceOrigin;
  label?: string;
  typeRegister?: string;
  required?: boolean;
  fromDate?: DateTime | null;
  onChangeResidencyOriginField: (
    key: keyof ResidenceOrigin,
    value: ResidenceOrigin[keyof ResidenceOrigin]
  ) => void;
  idAnpr?: string;
  sipoSerialNumber?: number;
  onChangeResidencyOriginFields?: (values: ResidenceOrigin) => void;
}

const ResidenceOriginForm = ({
  values,
  required,
  typeRegister,
  fromDate,
  onChangeResidencyOriginField,
  idAnpr,
  sipoSerialNumber,
  onChangeResidencyOriginFields,
}: ResidenceOriginFormProps): React.ReactElement => {
  const params = useParams();
  const formContext = useFormikContext<PracticeDetailVM>();

  const updateResidencyOrigin = useHttpClient(
    `${__PRACTICE_API_URL__}/rettifica-pratiche/rettifica-provenienza`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onConfirmResidenceOriginForm = useEventCallback(() => {
    const italianMunicipality = {
      comune: {
        nomeComune: values?.origin?.description || undefined,
        codiceIstat: values?.origin?.istatCode || undefined,
        siglaProvinciaIstat: values?.origin?.province || undefined,
      },
    };

    const foreignMunicipality = {
      localita: {
        descrizioneStato: values?.originState?.value || undefined,
        codiceStato: values?.originState?.id || undefined,
        provinciaContea: values?.originMunicipalityForeign?.province || undefined,
        descrizioneLocalita: values?.originMunicipalityForeign?.description || undefined,
      },
    };

    const data = praticePersonResidenceOriginUpdateTransformer({
      foreignMunicipality,
      italianMunicipality,
      idAnpr,
      sipoSerialNumber,
      params,
      residenceOrigin: values,
    });

    (
      updateResidencyOrigin({
        data,
      }) as AxiosPromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
    });
  });

  const disabled: boolean = useMemo(() => {
    return formContext.values.practice?.codState === '9';
  }, [formContext.values.practice?.codState]);

  const onChangeMunicipality = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    onChangeResidencyOriginField('origin', municipality);
  });

  const onChangeForeignState = useEventCallback(
    (foreignState: ForeignStateVM | null | undefined) => {
      if (onChangeResidencyOriginFields) {
        onChangeResidencyOriginFields({
          ...values,
          originState: foreignState,
          originMunicipalityForeign: null,
        });
      }
    }
  );

  const onChangeForeignMunicipality = useEventCallback(
    (foreignMunicipality: MunicipalityAnprVM | null) => {
      onChangeResidencyOriginField('originMunicipalityForeign', foreignMunicipality);
    }
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Provenienza"
            id="isForeign"
            value={values?.isForeign || 'false'}
            onChange={(event) => {
              onChangeResidencyOriginField('isForeign', event.target.value);
            }}
          >
            <FormControlLabel
              onClick={noop}
              value="false"
              control={<Radio />}
              label="Italia"
              disabled={disabled}
            />
            <FormControlLabel
              onClick={noop}
              value="true"
              control={<Radio />}
              label="Estero"
              disabled={disabled}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!values?.isForeign ? (
        <Grid item xs={12} md={9} container spacing={1}>
          <Grid item xs={12} md={8}>
            <MunicipalityList
              onChange={onChangeMunicipality}
              value={values?.origin || null}
              label={`Comune ${required ? '*' : ''}`}
              fromDate={fromDate}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              aria-label="Provincia"
              label="Provincia"
              id="province"
              name="province"
              fullWidth
              disabled={disabled}
              type="text"
              variant="outlined"
              value={values?.origin?.province || ''}
              onChange={noop}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid item xs={12} md={9} container spacing={1}>
          <Grid item xs={12} md={6}>
            <ForeignStateList
              onChange={onChangeForeignState}
              value={values?.originState || null}
              label={`Stato Estero ${required ? '*' : ''}`}
              id="foreignState"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MunicipalityForeignList
              onChange={onChangeForeignMunicipality}
              value={values?.originMunicipalityForeign || null}
              label="Comune Estero"
              id="foreignMunicipality"
              disabled={disabled}
              foreignStateIstatCode={values?.originState?.id}
            />
          </Grid>
        </Grid>
      )}
      <Grid item xs={12} md={9} container spacing={1}>
        <Grid item xs={12} md={3} display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                id="irreperibile"
                checked={values?.irreperibile || false}
                inputProps={{ 'aria-label': 'Irreperibile' }}
                disabled={disabled}
                onChange={(event) => {
                  onChangeResidencyOriginField('irreperibile', !values?.irreperibile);
                  if (onChangeResidencyOriginFields) {
                    onChangeResidencyOriginFields({
                      irreperibile: event.target.checked,
                      isForeign: values?.isForeign || false,
                      origin: !values?.irreperibile ? undefined : null,
                      originMunicipalityForeign: !values?.irreperibile ? undefined : null,
                      originState: !values?.irreperibile ? undefined : null,
                    });
                  }
                }}
              />
            }
            label="Irreperibile"
          />
        </Grid>
        {typeRegister === TypeRegister.Immigration ? (
          <Grid item xs={12} md={4} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  id="neverRegistered"
                  checked={values?.neverRegistered || false}
                  inputProps={{ 'aria-label': 'Mai anagrafato' }}
                  disabled={disabled}
                  onChange={() => {
                    onChangeResidencyOriginField('neverRegistered', !values?.neverRegistered);
                  }}
                />
              }
              label="Mai anagrafato"
            />
          </Grid>
        ) : null}
      </Grid>
      <Grid item xs={12} md={6}>
        <MunicipalityAutoComplete
          selectedValue={{ description: values?.previousOrigin ? values.previousOrigin : '' }}
          label="Comune precedente iscrizione"
          params={{
            italia: false,
            irreperibile: true,
          }}
          handleChange={(value) => {
            if (!onChangeResidencyOriginFields) return;
            onChangeResidencyOriginFields({
              ...values,
              previousOrigin: value?.description || '',
              previousOriginProvince: value?.province || '',
              previousOriginCode: value?.code || '',
              previousOriginSipoCode: value?.code || '',
            });
            /*   onChangeResidencyOriginField('previousOrigin', value?.description || '');
            onChangeResidencyOriginField('previousOriginProvince', value?.province || ''); */
          }}
          error={false}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          aria-label="Provincia precedente iscrizione"
          label="Provincia precedente iscrizione"
          id="previousOriginProvince"
          name="previousOriginProvince"
          fullWidth
          disabled={disabled}
          type="text"
          variant="outlined"
          value={values?.previousOriginProvince || ''}
          onChange={(event) => {
            onChangeResidencyOriginField('previousOriginProvince', event.target.value || '');
          }}
        />
      </Grid>
      <Grid item xs={12} md={4} marginTop={2}>
        <PmiButton
          variant="outlined"
          color="cdmBrandThird"
          onClick={onConfirmResidenceOriginForm}
          fullWidth
          disabled={disabled}
        >
          CONFERMA
        </PmiButton>
      </Grid>
    </Grid>
  );
};

ResidenceOriginForm.displayName = 'Residence Origin Form Component for interested person';

export default memo(ResidenceOriginForm);
